import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const scrollToNext = () => {
    const el = document.getElementById("legacy");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative w-full h-screen min-h-[680px] flex flex-col items-center justify-center overflow-hidden bg-[#1c120b]">

      {/* ── BACKGROUND IMAGE with Ken Burns zoom ─────────────── */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=opulent%20royal%20palace%20interior%20grand%20ornate%20sofa%20set%20with%20gold%20carved%20wooden%20frames%20upholstered%20in%20deep%20royal%20burgundy%20and%20ivory%20silk%20velvet%20tufted%20cushions%20majestic%20four%20poster%20canopy%20bed%20with%20embroidered%20silk%20drapes%20gilded%20carvings%20crystal%20chandelier%20above%20gleaming%20marble%20floor%20Persian%20silk%20rug%20heavy%20brocade%20curtains%20warm%20golden%20candlelight%20aristocratic%20European%20heritage%20grand%20ballroom%20old%20world%20luxury%20estate%20dramatic%20editorial%20photography%20cinematic%20chiaroscuro%20lighting%20timeless%20regal%20craftsmanship%20deep%20shadows%20moody%20atmosphere&width=1920&height=1080&seq=hero-royal-palace-v2&orientation=landscape"
          alt="M. Hayat & Co. — Handcrafted Luxury Furniture"
          className="w-full h-full object-cover object-center animate-kenBurns"
          style={{ transformOrigin: "center center" }}
        />
      </div>

      {/* ── LAYERED OVERLAYS ──────────────────────────────────── */}
      {/* Base dark veil */}
      <div className="absolute inset-0 bg-[#110b06]/55" />
      {/* Radial vignette: edges darker, center lighter */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 30%, rgba(17,11,6,0.65) 100%)",
        }}
      />
      {/* Top fade for navbar legibility */}
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-[#1a0f0a]/80 to-transparent" />
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a0f0a]/70 to-transparent" />

      {/* ── GRAIN TEXTURE OVERLAY ─────────────────────────────── */}
      <div
        className="absolute inset-[-20%] opacity-[0.04] pointer-events-none z-10 animate-grainDrift"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundSize: "280px 280px",
        }}
      />

      {/* ── CENTRAL CONTENT ───────────────────────────────────── */}
      <div className="relative z-20 flex flex-col items-center text-center px-6 md:px-12 w-full max-w-4xl mx-auto">

        {/* Est. 1870 badge */}
        <div
          className={`transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-6"}`}
          style={{ transitionDelay: "0.3s" }}
        >
          <div className="inline-flex items-center gap-4 mb-8 md:mb-10">
            <div className="h-px w-10 md:w-16 bg-brand-gold/50" />
            <span className="font-display text-[9px] md:text-[10px] tracking-[5px] uppercase text-brand-gold/80">
              Est. 1870
            </span>
            <div className="h-px w-10 md:w-16 bg-brand-gold/50" />
          </div>
        </div>

        {/* Main headline */}
        <div className="overflow-hidden mb-1">
          <h1
            className={`font-serif text-[2.4rem] sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[1.05] tracking-tight transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            style={{ transitionDelay: "0.55s" }}
          >
            Since 1870
          </h1>
        </div>

        {/* Decorative thin rule */}
        <div
          className={`flex items-center gap-4 my-4 md:my-5 w-full max-w-xs md:max-w-sm transition-all duration-1000 ${loaded ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"}`}
          style={{ transitionDelay: "0.75s", transformOrigin: "center" }}
        >
          <div className="flex-1 h-px bg-brand-gold/35" />
          <div className="w-1 h-1 rounded-full bg-brand-gold/50" />
          <div className="flex-1 h-px bg-brand-gold/35" />
        </div>

        <div className="overflow-hidden mb-1">
          <h2
            className={`font-serif text-[2.4rem] sm:text-5xl md:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            style={{ transitionDelay: "0.85s" }}
          >
            <span className="text-brand-goldlight">Crafting Timeless</span>
          </h2>
        </div>

        <div className="overflow-hidden">
          <p
            className={`font-serif text-[2.4rem] sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[1.05] tracking-tight transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            style={{ transitionDelay: "1.0s" }}
          >
            Furniture
          </p>
        </div>

        {/* Subheadline */}
        <div
          className={`mt-6 md:mt-8 transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: "1.15s" }}
        >
          <p className="font-display text-[11px] md:text-xs tracking-[4px] uppercase text-white/55">
            Where Heritage Meets Modern Living
          </p>
        </div>

        {/* Subtext */}
        <div
          className={`mt-4 transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "1.25s" }}
        >
          <p className="font-sans text-sm text-white/40 leading-relaxed max-w-[320px] md:max-w-md mx-auto">
            Six generations of master craftsmen, shaping wood into living legacy. Each piece is a testament to a rare art form preserved across centuries.
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row items-center gap-4 mt-9 md:mt-11 w-full sm:w-auto transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: "1.4s" }}
        >
          {/* Primary CTA */}
          <Link
            to="/collections"
            className="whitespace-nowrap relative overflow-hidden inline-flex items-center justify-center gap-3 font-display text-[11px] tracking-[3.5px] uppercase px-9 py-4 rounded-full cursor-pointer group border border-brand-gold text-brand-gold transition-all duration-400 hover:text-brand-darkbrown"
            style={{ transition: "color 0.4s ease" }}
          >
            {/* Fill animation */}
            <span
              className="absolute inset-0 bg-brand-gold rounded-full translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out"
              aria-hidden="true"
              style={{ transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)" }}
            />
            <span className="relative z-10">Explore Collections</span>
            <i className="ri-arrow-right-line relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          {/* Secondary CTA */}
          <button
            onClick={() => {
              const el = document.getElementById("gallery-preview");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="whitespace-nowrap inline-flex items-center justify-center gap-2 font-display text-[11px] tracking-[3.5px] uppercase px-8 py-4 rounded-full cursor-pointer text-white/60 hover:text-brand-gold transition-colors duration-300"
          >
            <i className="ri-play-circle-line text-base" />
            View Gallery
          </button>
        </div>
      </div>

      {/* ── SCROLL INDICATOR ──────────────────────────────────── */}
      <button
        onClick={scrollToNext}
        aria-label="Scroll down"
        className={`absolute bottom-5 md:bottom-4 left-1/2 -translate-x-1/2 z-20 cursor-pointer group transition-all duration-1000 ${loaded ? "opacity-100" : "opacity-0"}`}
        style={{ transitionDelay: "1.8s" }}
      >
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center pt-1.5 group-hover:border-brand-gold/50 transition-colors duration-300">
            <div className="w-0.5 h-2 bg-brand-gold/60 rounded-full animate-bounce" />
          </div>
        </div>
      </button>

      {/* ── CORNER ACCENT — top right (desktop) ──────────────── */}
      <div
        className={`hidden lg:block absolute top-28 right-14 z-20 transition-all duration-1200 ${loaded ? "opacity-100" : "opacity-0"}`}
        style={{ transitionDelay: "1.4s" }}
      >
        <div className="relative w-14 h-14">
          <div className="absolute top-0 right-0 w-full h-px bg-brand-gold/40" />
          <div className="absolute top-0 right-0 w-px h-full bg-brand-gold/40" />
        </div>
      </div>

      {/* ── CORNER ACCENT — bottom left (desktop) ────────────── */}
      <div
        className={`hidden lg:block absolute bottom-28 left-14 z-20 transition-all duration-1200 ${loaded ? "opacity-100" : "opacity-0"}`}
        style={{ transitionDelay: "1.4s" }}
      >
        <div className="relative w-14 h-14">
          <div className="absolute bottom-0 left-0 w-full h-px bg-brand-gold/40" />
          <div className="absolute bottom-0 left-0 w-px h-full bg-brand-gold/40" />
        </div>
      </div>

      {/* ── CRAFT BADGE — bottom right (desktop) ─────────────── */}
      <div
        className={`hidden lg:flex absolute bottom-28 right-14 z-20 items-center gap-3 transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        style={{ transitionDelay: "1.5s" }}
      >
        <div className="border border-brand-gold/25 rounded-lg px-4 py-3 bg-[#1a0f0a]/50 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center text-brand-gold">
              <i className="ri-medal-line text-lg" />
            </div>
            <div>
              <div className="text-white text-xs font-serif font-semibold leading-tight">100% Handcrafted</div>
              <div className="text-white/45 text-[9px] tracking-[2px] uppercase font-display mt-0.5">Master Artisans</div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
